import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, FormControl, Select, MenuItem, TextField,
  CircularProgress, Alert, Tooltip, IconButton, Chip
} from '@mui/material';
import { Search, Refresh, Description, Receipt, Download, ChevronLeft, ChevronRight, TrendingUp, TrendingDown } from '@mui/icons-material';
import { getWalletAdjustmentsReport } from '../../services/adminApi';
import { showWarningToast, showErrorToast, showSuccessToast } from '../../utils/toast';
import { formatDateTimeDDMMYYYY } from '../../utils/dateUtils';

const fmt = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount ?? 0);

const WalletAdjustmentsReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filters
  const [merchantId, setMerchantId] = useState('');
  const [operationType, setOperationType] = useState('ALL');
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  // Pagination & Data
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const PAGE_SIZE = 20;

  const fetchReport = async (targetPage = 1) => {
    if (new Date(toDate) < new Date(fromDate)) {
      showErrorToast('To Date cannot be earlier than From Date.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const params = {
        fromDate,
        toDate,
        page: targetPage - 1, // assuming zero-indexed pagination if backend supports it
        size: PAGE_SIZE
      };

      if (merchantId) params.merchantId = merchantId;
      if (operationType !== 'ALL') params.operationType = operationType;

      const response = await getWalletAdjustmentsReport(params);
      const resData = response.data;
      
      let data = [];
      if (resData?.content || resData?.data) {
        data = resData.content || resData.data;
        setTotalRecords(resData.totalElements || resData.totalRecords || data.length);
        setTotalPages(resData.totalPages || Math.ceil((resData.totalElements || data.length) / PAGE_SIZE) || 1);
      } else if (Array.isArray(resData)) {
        data = resData;
        setTotalRecords(data.length);
        setTotalPages(Math.ceil(data.length / PAGE_SIZE));
      }

      setReports(data);
      setPage(targetPage);
    } catch (err) {
      console.error('Failed to fetch adjustments report:', err);
      const apiErrorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to load report. Please try again.';
      setError(apiErrorMsg);
      showErrorToast(apiErrorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchReport(newPage);
    }
  };

  const exportData = () => {
    if (!reports || reports.length === 0) {
      showWarningToast('No data to export.');
      return;
    }

    const escapeCsv = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

    const headers = [
      'Date', 'Transaction ID', 'Merchant ID', 'Merchant Name', 
      'Type', 'Amount', 'Balance Before', 'Balance After', 
      'Reference ID', 'Remarks / Description'
    ];
    
    const rows = reports.map(t => [
      formatDateTimeDDMMYYYY(t.createdAt || t.date),
      t.transactionId || t.txnId || '-',
      t.merchantId || '-',
      t.merchantName || '-',
      t.transactionType || t.operationType || '-',
      t.amount || 0,
      t.balanceBefore || 0,
      t.balanceAfter || 0,
      t.referenceId || '-',
      t.description || t.remarks || '-'
    ].map(escapeCsv));

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wallet-adjustments-report-${fromDate}-to-${toDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    showSuccessToast('Report successfully exported!');
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
      {/* ── Page Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Wallet Adjustments Report</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View and export manual credit and debit adjustments across merchant wallets
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={exportData}
            disabled={loading || reports.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#4f46e5', borderColor: '#4f46e5' }}
          >
            Export CSV
          </Button>
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => fetchReport(page)} disabled={loading} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Refresh sx={{ fontSize: 18, ...(loading && { animation: 'spin 1s linear infinite' }) }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filter Bar ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ py: '14px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Description sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Report Filters</Typography>
          </Box>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2.5}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Merchant ID</Typography>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                placeholder="All Merchants"
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13, bgcolor: '#fff' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>From Date</Typography>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                style={{
                  width: '100%', padding: '8.5px 12px', border: '1px solid #c4c4c4', borderRadius: 8,
                  fontSize: 13, color: '#1e293b', outline: 'none', fontFamily: 'inherit', backgroundColor: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>To Date</Typography>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                style={{
                  width: '100%', padding: '8.5px 12px', border: '1px solid #c4c4c4', borderRadius: 8,
                  fontSize: 13, color: '#1e293b', outline: 'none', fontFamily: 'inherit', backgroundColor: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Operation Type</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={operationType}
                  onChange={e => setOperationType(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: 2, fontSize: 13, bgcolor: '#fff' }}
                >
                  <MenuItem value="ALL">All Types</MenuItem>
                  <MenuItem value="CREDIT">Credit</MenuItem>
                  <MenuItem value="DEBIT">Debit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Search sx={{ fontSize: 15 }} />}
                  onClick={() => fetchReport(1)}
                  fullWidth
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                >
                  Search
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Report Data Table ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: '0 !important' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : reports.length > 0 ? (
            <>
              <TableContainer sx={{ maxHeight: '60vh', overflowX: 'auto' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#4b5563', bgcolor: '#f9fafb' }}>Date & Txn ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#4b5563', bgcolor: '#f9fafb' }}>Merchant</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#4b5563', bgcolor: '#f9fafb' }}>Remarks & Ref</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#4b5563', bgcolor: '#f9fafb' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#4b5563', bgcolor: '#f9fafb', textAlign: 'right' }}>Balances</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#4b5563', bgcolor: '#f9fafb', textAlign: 'right' }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((r, idx) => (
                      <TableRow key={r.id || idx} hover>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="caption" sx={{ color: '#4b5563', display: 'block' }}>
                            {formatDateTimeDDMMYYYY(r.createdAt || r.date, false)}
                          </Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937' }}>
                            {r.transactionId || r.txnId || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.merchantName || '—'}</Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280' }}>{r.merchantId || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: '#4b5563', display: 'block' }}>{r.description || r.remarks || '—'}</Typography>
                          {r.referenceId && <Typography variant="caption" sx={{ color: '#6b7280', fontFamily: 'monospace' }}>Ref: {r.referenceId}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            size="small" 
                            label={r.transactionType || r.operationType || '—'} 
                            icon={(r.transactionType === 'CREDIT' || r.operationType === 'CREDIT') ? <TrendingUp /> : <TrendingDown />}
                            sx={{ 
                              fontWeight: 700, fontSize: 10,
                              bgcolor: (r.transactionType === 'CREDIT' || r.operationType === 'CREDIT') ? '#dcfce7' : '#fee2e2',
                              color: (r.transactionType === 'CREDIT' || r.operationType === 'CREDIT') ? '#166534' : '#991b1b',
                              '& .MuiChip-icon': { color: 'inherit' }
                            }} 
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Op: {fmt(r.balanceBefore)}</Typography>
                          <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600, display: 'block' }}>Cl: {fmt(r.balanceAfter)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ 
                            fontWeight: 700, fontSize: 14, 
                            color: (r.transactionType === 'CREDIT' || r.operationType === 'CREDIT') ? '#16a34a' : '#dc2626' 
                          }}>
                            {(r.transactionType === 'CREDIT' || r.operationType === 'CREDIT') ? '+' : '-'}{fmt(r.amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#f9fafb' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: 13 }}>
                    {totalRecords > 0 ? `Showing page ${page} of ${totalPages} (${totalRecords} records)` : `Showing ${reports.length} records`}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page <= 1}
                      onClick={() => handlePageChange(page - 1)}
                      startIcon={<ChevronLeft />}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page >= totalPages}
                      onClick={() => handlePageChange(page + 1)}
                      endIcon={<ChevronRight />}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Receipt sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
              <Typography variant="body2">No adjustment records found for the selected filters</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WalletAdjustmentsReport;
